import React, { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CollapsibleCard from "components/CollapsibleCard";
import InnerTable from "lib/Tables/InnerTable"; // Assuming this is your table component
import { Edit, Delete, Save, WarningAmber } from "@mui/icons-material";
import { Checkbox, TextField } from "@mui/material"; // For editable description
import { useStoreActions, useStoreState } from "easy-peasy";
import EditableTable from "lib/Tables/EditableTable";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { del } from "requests";
import MDTypography from "components/MDTypography";
import WarningDialog from "components/WarningDialog";

interface CommissionProps {
  dealApplicationId: number;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
}

const Commission: React.FC<CommissionProps> = ({ dealApplicationId, setComponentLoading }) => {
  const onInit = useStoreActions((actions: any) => actions.heldBackCommissionStore.onInit);

  const handlePostCommissionUpdate = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.handlePostComissionUpdate
  );

  const saveCommission = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.saveCommission
  );

  const getContractTerms = useStoreActions((actions: any) => actions.offerStore.getContractTerms);
  const saveContractTerms = useStoreActions((actions: any) => actions.offerStore.saveContractTerms);
  const updateBankFeeOfBrokerAsInvestor = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.updateBankFeeOfBrokerAsInvestor
  );
  const setInvestorBankFeeRequest = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.setInvestorBankFeeRequest
  );
  const investorBankFeeRequest = useStoreState(
    (state: any) => state.heldBackCommissionStore.investorBankFeeRequest
  );

  const deleteCommission = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.deleteCommission
  );
  const comissions = useStoreState((state: any) => state.heldBackCommissionStore.commissions);
  const brokersList = useStoreState((state: any) => state.heldBackCommissionStore.brokersList);

  const bankFeeAlert = useStoreState((state: any) => state.heldBackCommissionStore.bankFeeAlert);
  const syndicationBankFeeAlert = useStoreState(
    (state: any) => state.heldBackCommissionStore.syndicatorBankFeeAlert
  );

  const bankFeeMessage = useStoreState(
    (state: any) => state.heldBackCommissionStore.bankFeeMessage
  );
  const setBankFeeMessage = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.setBankFeeMessage
  );
  const commissionAmount = useStoreState(
    (state: any) => state.heldBackCommissionStore.commissionAmount
  );
  const totalCommission = useStoreState(
    (state: any) => state.heldBackCommissionStore.totalCommission
  );

  const setBankFeeAlert = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.setBankFeeAlert
  );
  const setSyndicationBankFeeAlert = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.setSyndicatorBankFeeAlert
  );

  const commissionTypes = useStoreState(
    (state: any) => state.heldBackCommissionStore.commissionTypes
  );
  const [commissionData, setCommissionData] = useState([]);
  const contractTerms = useStoreState((state: any) => state.offerStore.contractTerms);

  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortedColumn, setSortedColumn] = useState<string | null>("amount");
  const refreshSynd = useStoreActions((actions: any) => actions.syndicationStore.fetchSyndicators);
  const updateSyndicatorsPercentage = useStoreActions(
    (actions: any) => actions.syndicationStore.updateSyndicatorsPercentage
  );

  const setLoading = useStoreActions((actions: any) => actions.offerStore.setLoading);

  useEffect(() => {
    try {
      setComponentLoading("commission", false);
      setCommissionData(comissions);
    } finally {
      setComponentLoading("commission", false);
    }
  }, [comissions]);

  const isDefaultBroker = () => {
    const innerContractTerms: any = contractTerms?.contractTerms;
    if (!innerContractTerms?.["funder"]) return false;

    const funderDefaultSyndicatorName = innerContractTerms["funder"]?.["defaultSyndicatorName"];
    const funderName = innerContractTerms["funder"]?.["name"];
    const broker = brokersList?.find(
      (broker: any) => broker?.id === editingRow?.applicationPartyId
    );

    if (!broker) return false;

    const brokerName = broker?.name;

    return funderDefaultSyndicatorName === brokerName || funderName === brokerName;
  };

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const brokersOptions = brokersList?.map((broker: any) => {
    return {
      label: broker?.name,
      value: broker?.id,
    };
  });

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      const deletingObject = commissionData.find((item) => item.id === id);
      if (!deletingObject) throw new Error("Commission data not found");

      const type = deletingObject.type;
      const offerDetails = await getContractTerms(dealApplicationId);

      await deleteCommission({
        id: id,
        type,
        callback: async () => {
          await handlePostCommissionUpdate({
            dealApplicationId: dealApplicationId,
            offerDetails: offerDetails,
            callback: saveContractTerms,
          });
          await getContractTerms(dealApplicationId);
        },
        applicationId: dealApplicationId,
      });
      await refreshSynd(dealApplicationId);
    } catch (error) {
      console.error("Error deleting commission: ", error);
    } finally {
      setLoading(false);
    }
  };

  const formatLabel = (label: any) => {
    if (!label) return "";

    return label
      .replace(/_/g, " ")
      .split(" ")
      .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const columns = [
    {
      Header: "ISO/Broker Name",
      accessor: "applicationPartyName",
      selector: "applicationPartyId",
      align: "left",
      input: "customSelect",
      options: brokersOptions,
      width: 150,
    },
    {
      Header: "COMMISSION TYPE",
      accessor: "commissionTypeName",
      selector: "commissionTypeId",
      width: 150,
      align: "left",
      input: "select",
      disabled: true,
      options: commissionTypes,
      Cell: (row: any) => formatLabel(row?.row?.commissionTypeName),
    },
    {
      Header: "AMOUNT",
      accessor: "amount",
      align: "left",
      input: "amount",
      width: 120,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.amount),
    },
    {
      Header: "% Of funding",
      accessor: "percentageOfCommission",
      input: "numeric",
      align: "left",
      width: 120,
      required: false,
    },
    {
      Header: "No. Of Days",
      accessor: "dayIndex",
      align: "left",
      input: "number",
      required: false,
      width: 120,
    },
    {
      Header: "COMMENTS",
      accessor: "description",
      align: "left",
      required: false,
      width: 180,
    },
    {
      Header: "Invest In Deal",
      accessor: "isInvestor",
      input: "checkbox",
      width: 100,
      disabled: isDefaultBroker,
      Cell: (row: any) => (
        <input type="checkbox" checked={row?.row?.isInvestor} disabled={true} onChange={() => {}} />
      ),
    },
  ];

  const onSave = async () => {
    if (!editingRow || !editingRow?.amount) {
      return;
    }
    try {
      setLoading(true);
      editingRow["applicationId"] = dealApplicationId;
      const offerDetails = await getContractTerms(dealApplicationId);
      await saveCommission({
        commissionData: editingRow,
        offerDetails: offerDetails,
        updateSyndicatorsPercentage: updateSyndicatorsPercentage,
      });
      setEditingRow(null);
      await handlePostCommissionUpdate({
        dealApplicationId: dealApplicationId,
        offerDetails: offerDetails,
        callback: undefined,
      });
      await getContractTerms(dealApplicationId);
      await refreshSynd(dealApplicationId);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewBroker = (value: string): boolean => {
    let updatedRow = { ...editingRow };

    if (!value) {
      updatedRow["showAddingNew"] = false;
      updatedRow["applicationPartyName"] = null;
      updatedRow["applicationPartyId"] = null;
      setEditingRow(updatedRow);
      return true;
    }

    const nameExists = brokersList?.some(
      (broker: any) => broker?.name?.toLowerCase() === value.toLowerCase()
    );

    if (nameExists) {
      return false;
    }

    updatedRow["newBrokerParty"] = value;
    updatedRow["addNewHeldbackBrokerParty"] = true;
    updatedRow["applicationPartyName"] = null;
    updatedRow["applicationPartyId"] = null;
    setEditingRow(updatedRow);

    return true;
  };

  const handleEditComission = (row: any | null) => {
    const commissionTypeId = commissionTypes[0].value;

    const commissionType: any = commissionTypes.find(
      (comm: any) => comm?.value?.name === editingRow?.commissionTypeName
    );

    const newEditingRow = row ? { ...row } : {};

    if (commissionType) {
      newEditingRow["commissionTypeId"] = commissionType?.value;
    } else {
      newEditingRow["commissionTypeId"] = commissionTypeId;
    }

    setIsEditing(true);
    setEditingRow(newEditingRow);
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setIsEditing(false);
  };

  const handleInputChange = (value: any, field: string) => {
    let updatedRow = { ...editingRow };

    if (field === "addNew") {
      updatedRow["applicationPartyName"] = null;
      updatedRow["applicationPartyId"] = null;
      updatedRow["showAddingNew"] = true;
      setEditingRow(updatedRow);
      return;
    }

    const fundingAmount = contractTerms?.fundedAmount;

    if (field === "amount" && fundingAmount && !isNaN(fundingAmount) && !isNaN(value)) {
      const commissionPercent = (value / fundingAmount) * 100;
      const formatted = parseFloat(commissionPercent.toFixed(4));

      setEditingRow((prev: any) => ({
        ...prev,
        percentageOfCommission: formatted,
        [field]: value,
      }));
    } else if (
      field === "percentageOfCommission" &&
      fundingAmount &&
      !isNaN(fundingAmount) &&
      !isNaN(value)
    ) {
      const commission = (value / 100) * fundingAmount;
      const formatted = parseFloat(commission.toFixed(2));

      setEditingRow((prev: any) => ({
        ...prev,
        amount: formatted,
        [field]: value,
      }));
    } else {
      setEditingRow((prev: any) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSelectRow = (rowKey: number) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(rowKey)
        ? prevSelected.filter((key) => key !== rowKey)
        : [...prevSelected, rowKey]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === commissionData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(commissionData.map((row: any) => row.id));
    }
  };

  const handleSort = (columnKey: string) => {
    const isAsc = sortedColumn === columnKey && sortDirection === "asc";
    setSortedColumn(columnKey);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const getRowStyle = (row: any) => {
    return "regular";
  };

  const handleAddNewComission = () => {
    const newComisssion: any = {
      applicationPartyName: undefined,
      applicationPartyId: undefined,
      commissionTypeName: "",
      commissionTypeId: commissionTypes[0].value,
      dayIndex: "",
      description: "",
      isInvestor: false,
    };
    setEditingRow(newComisssion);
    setIsEditing(true);
  };

  const renderTotal = (key: any) => {
    let total = 0;

    if (editingRow && editingRow[key]) {
      const editingAmount = parseFloat(editingRow[key]);
      if (!isNaN(editingAmount)) {
        total += editingAmount;
      }
    }

    comissions
      .filter((row: any) => !editingRow || editingRow.id !== row.id)
      .forEach((row: any) => {
        const rowAmount = parseFloat(row[key]);
        if (!isNaN(rowAmount)) {
          total += rowAmount;
        }
      });

    return key.includes("amount")
      ? formatCurrencyUSD(total.toFixed(2))
      : key.includes("percentage")
      ? `${total.toFixed(2)} %`
      : total.toFixed(2);
  };

  const customButton = (
    <MDButton
      color="primary"
      variant="contained"
      size="medium"
      sx={{ textTransform: "none" }}
      onClick={handleAddNewComission}
    >
      + Commission
    </MDButton>
  );

  const renderTable = () => {
    return (
      <EditableTable
        rowKey={"id"}
        columns={columns}
        data={commissionData}
        onDelete={handleDelete}
        onEdit={handleEditComission}
        onSave={onSave}
        onCancel={handleCancelEdit}
        editingRow={editingRow}
        isEditing={isEditing}
        onInputChange={handleInputChange}
        selectedRows={selectedRows}
        onSelectRow={handleSelectRow}
        onSelectAll={handleSelectAll}
        onSort={handleSort}
        getRowStyle={getRowStyle}
        isActionsDisabled={undefined}
        renderTotal={renderTotal}
        totalColumn={["amount", "percentageOfCommission"]}
        onAddNewSubmit={handleAddNewBroker}
      />
    );
  };

  return (
    <CollapsibleCard
      header={
        <>
          Commission
          {commissionAmount !== totalCommission && (
            <MDTypography sx={{ color: "red", fontSize: "0.875rem" }}>
              {" "}
              Total commission is not equal to Commission Amount
            </MDTypography>
          )}
        </>
      }
      customButton={customButton}
    >
      <MDBox>
        {bankFeeAlert && (
          <WarningDialog
            open={bankFeeAlert}
            onClose={() => {
              setBankFeeAlert(false);
              setBankFeeMessage("");
              setInvestorBankFeeRequest(null);
            }}
            message={bankFeeMessage}
            onConfirm={() => {
              updateBankFeeOfBrokerAsInvestor(investorBankFeeRequest);

              setBankFeeAlert(false);
              setBankFeeMessage("");
              setInvestorBankFeeRequest(null);
            }}
            confirmText="Confirm"
            cancelText="Cancel"
            showActions={true}
            type="warning"
          />
        )}

        {syndicationBankFeeAlert && (
          <WarningDialog
            open={!!syndicationBankFeeAlert}
            onClose={() => setSyndicationBankFeeAlert(null)}
            message={syndicationBankFeeAlert}
            type="warning"
            showActions={true}
          />
        )}
        {renderTable()}
      </MDBox>
    </CollapsibleCard>
  );
};

export default Commission;
